import downArrow from "@assets/quick-news/down-arrow.svg";
import Paragraph from "@elements/Typography/Paragraph";
import { countWords, truncateWords } from "@helper/stringTransform";
import { ProcessedArticleBody } from "@typings/Opensearch";
import React, { type ReactElement, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const WORD_COUNT = 80;
const MAX_PARAGRAPHS = 4;
const TOGGLE_BUTTON_BUFFER = 25; // Buffer for toggle button

interface QnParagraphProps {
  articleBody: ProcessedArticleBody;
  toggleDescription: () => void;
  isDescriptionOpen: boolean;
  setReduceSize: React.Dispatch<React.SetStateAction<number>>;
}

export default function QnParagraph({
  articleBody,
  toggleDescription,
  isDescriptionOpen,
  setReduceSize,
}: QnParagraphProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !contentRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.offsetHeight;

      setIsOverflowing(contentHeight > containerHeight - TOGGLE_BUTTON_BUFFER);

      if (contentHeight > containerHeight - TOGGLE_BUTTON_BUFFER) {
        setReduceSize(contentHeight - containerHeight + TOGGLE_BUTTON_BUFFER);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [setReduceSize]);

  const renderParagraphs = () => {
    let accumulatedWords = 0;
    let paragraphCounter = 0;

    return articleBody.map((element, index) => {
      if (element.type !== "paragraph") return null;

      if (paragraphCounter >= MAX_PARAGRAPHS || accumulatedWords >= WORD_COUNT)
        return null;

      const wordsInParagraph = countWords(element.value ? element.value : "");
      if (wordsInParagraph < 1) return null;

      let wordsToShow = wordsInParagraph;

      if (accumulatedWords + wordsInParagraph > WORD_COUNT) {
        wordsToShow = WORD_COUNT - accumulatedWords;
      }

      accumulatedWords += wordsToShow;
      paragraphCounter += 1;

      return (
        <Paragraph
          key={element.id || index}
          testid={`paragraph-${index}`}
          // id={element.id}
          className={twMerge(
            "qn-article-description text-grey-550 text-base font-normal tracking-normal leading-[150%] mb-0 duration-500 px-0 md:px-0",
          )}
        >
          {truncateWords(element.value ? element.value : "", wordsToShow)}
          {wordsToShow < wordsInParagraph && "..."}
        </Paragraph>
      );
    });
  };

  return (
    <div
      ref={containerRef}
      className="qn-article-body-container relative h-[calc(100lvh-490px)] md:h-[calc(100lvh-660px)]"
    >
      <div
        ref={contentRef}
        data-testid="article-body"
        className="qn-article-body gap-3 grid relative"
      >
        {renderParagraphs()}

        {/* Toggle button */}
        {isOverflowing && (
          <button
            onClick={toggleDescription}
            className={`qn-article-expand z-10 fixed bottom-[72px] flex w-[94%] justify-center mx-auto items-center ${!isDescriptionOpen && "collapsed"}`}
            aria-label="Toggle description"
          >
            <img
              src={downArrow}
              alt="dropdown-arrow"
              width={20}
              height={20}
              className={twMerge(
                "z-40 transition-transform duration-500",
                isDescriptionOpen && "-rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </div>
  );
}
